import {
  type PickerCandidate,
  UserSearchPicker,
  candidateKey,
} from '@/components/user/UserSearchPicker';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';
import type { DashboardDetail, SharePrincipal } from '@innermate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, Globe, Lock, Users, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dashboard: DashboardDetail;
}

type Access = 'private' | 'public';

/**
 * Notion 식 대시보드 공유 — 검색으로 개인 그룹/역할/개별 사용자를 골라 공유 대상에 추가하고,
 * 하단에서 일반 접근 권한(비공개/전체)을 설정한다. 공유는 보기 전용.
 */
export function DashboardShareDialog({ open, onOpenChange, dashboard }: Props) {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [access, setAccess] = useState<Access>('private');
  const [grants, setGrants] = useState<SharePrincipal[]>([]);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  // 현재 공유 현황 로드.
  const sharesQuery = useQuery({
    queryKey: ['dashboards', 'shares', dashboard.id],
    queryFn: () => api.dashboardShares(dashboard.id),
    enabled: open,
  });

  // 개인 그룹 + 내가 멤버인 역할 (검색 로컬 후보).
  const groupsQuery = useQuery({
    queryKey: ['user-groups', 'mine'],
    queryFn: api.userGroupsList,
    enabled: open,
    staleTime: 30_000,
  });
  const rolesQuery = useQuery({
    queryKey: ['dashboards', 'groups', 'mine'],
    queryFn: api.dashboardGroupOptions,
    enabled: open,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!open) return;
    setError(null);
    setCopyState('idle');
  }, [open]);

  useEffect(() => {
    if (!open || !sharesQuery.data) return;
    setAccess(sharesQuery.data.visibility === 'public' ? 'public' : 'private');
    setGrants(sharesQuery.data.grants);
  }, [open, sharesQuery.data]);

  const localCandidates: PickerCandidate[] = useMemo(() => {
    const groups: PickerCandidate[] = (groupsQuery.data?.groups ?? []).map((g) => ({
      kind: 'user_group',
      id: g.id,
      name: g.name,
      subtitle: `멤버 ${g.memberCount}명`,
    }));
    const roles: PickerCandidate[] = (rolesQuery.data?.groups ?? []).map((r) => ({
      kind: 'role',
      id: r.id,
      name: r.name,
      subtitle: r.description ?? '역할',
    }));
    return [...groups, ...roles];
  }, [groupsQuery.data, rolesQuery.data]);

  const excludeKeys = useMemo(
    () => new Set(grants.map((g) => candidateKey(g.principalType, g.principalId))),
    [grants],
  );

  const saveMutation = useMutation({
    mutationFn: () =>
      api.dashboardShare(dashboard.id, {
        visibility: access,
        grants: grants.map((g) => ({ principalType: g.principalType, principalId: g.principalId })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['dashboards', 'detail', dashboard.id] });
      qc.invalidateQueries({ queryKey: ['dashboards', 'mine'] });
      qc.invalidateQueries({ queryKey: ['dashboards', 'shares', dashboard.id] });
      onOpenChange(false);
    },
    onError: (err) => setError(err instanceof Error ? err.message : '저장 실패'),
  });

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/dashboard/view/${dashboard.id}`
      : `/dashboard/view/${dashboard.id}`;

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopyState('copied');
      window.setTimeout(() => setCopyState('idle'), 2000);
    } catch {
      setCopyState('error');
      window.setTimeout(() => setCopyState('idle'), 2000);
    }
  };

  const addGrant = (c: PickerCandidate) => {
    setGrants((prev) => [
      ...prev,
      { principalType: c.kind, principalId: c.id, name: c.name, subtitle: c.subtitle },
    ]);
  };
  const removeGrant = (g: SharePrincipal) => {
    setGrants((prev) =>
      prev.filter((p) => !(p.principalType === g.principalType && p.principalId === g.principalId)),
    );
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/50 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onOpenChange(false);
      }}
    >
      {/* biome-ignore lint/a11y/useSemanticElements: 다른 dialog 와 동일 패턴 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="대시보드 공유"
        className="w-full max-w-2xl rounded-2xl border border-border bg-surface shadow-[var(--shadow-card)] overflow-hidden flex flex-col max-h-[85vh]"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onOpenChange(false);
          }
        }}
      >
        <div className="px-6 pt-5 pb-1 text-base font-semibold">대시보드 공유</div>
        <div className="px-6 pb-3 text-xs text-foreground-muted">
          공유 대상은 대시보드를 볼 수 있습니다. 데이터는 보는 사람 본인 권한으로 다시 조회됩니다 —
          권한이 없는 데이터는 숨겨집니다.
        </div>

        <div className="px-6 pb-3">
          <UserSearchPicker
            excludeKeys={excludeKeys}
            onSelect={addGrant}
            localCandidates={localCandidates}
            placeholder="이름·이메일 또는 그룹으로 검색"
            autoFocus
          />
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-2">
          <ul className="space-y-0.5">
            {/* 소유자 행 (고정) */}
            <li className="flex items-center gap-3 rounded-md px-2 py-2">
              <span className="size-9 shrink-0 rounded-full bg-accent/15 text-accent grid place-items-center text-xs font-semibold">
                {(me?.displayName || me?.email || '?').slice(0, 1).toUpperCase()}
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">
                  {me?.displayName ?? '나'} <span className="text-foreground-muted">(나)</span>
                </div>
                <div className="text-xs text-foreground-muted truncate">{me?.email}</div>
              </div>
              <span className="shrink-0 text-xs text-foreground-muted">소유자</span>
            </li>

            {grants.map((g) => (
              <li
                key={candidateKey(g.principalType, g.principalId)}
                className="group flex items-center gap-3 rounded-md px-2 py-2 hover:bg-surface-muted"
              >
                <span
                  className={cn(
                    'size-9 shrink-0 rounded-full grid place-items-center',
                    g.principalType === 'user'
                      ? 'bg-accent/15 text-accent text-xs font-semibold'
                      : g.principalType === 'user_group'
                        ? 'bg-accent/15 text-accent'
                        : 'bg-surface-muted text-foreground-muted',
                  )}
                >
                  {g.principalType === 'user' ? (
                    g.name.slice(0, 1).toUpperCase()
                  ) : (
                    <Users className="size-4" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {g.name}
                    {g.principalType !== 'user' ? (
                      <span className="ml-1.5 rounded bg-surface-muted px-1.5 py-0.5 text-[10px] text-foreground-muted">
                        {g.principalType === 'user_group' ? '그룹' : '역할'}
                      </span>
                    ) : null}
                  </div>
                  {g.subtitle ? (
                    <div className="text-xs text-foreground-muted truncate">{g.subtitle}</div>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-foreground-muted">보기 허용</span>
                <button
                  type="button"
                  onClick={() => removeGrant(g)}
                  aria-label="공유 대상 제거"
                  className="shrink-0 grid size-7 place-items-center rounded-md text-foreground-muted opacity-0 group-hover:opacity-100 hover:bg-surface hover:text-destructive"
                >
                  <X className="size-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* 일반 접근 권한 */}
        <div className="border-t border-border px-6 py-3">
          <div className="mb-2 text-xs font-medium text-foreground-muted">일반 접근 권한</div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setAccess('private')}
              className={cn(
                'flex items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm',
                access === 'private'
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:bg-surface-muted',
              )}
            >
              <Lock className="size-4 text-foreground-muted" />
              <div>
                <div className="font-medium">비공개</div>
                <div className="text-xs text-foreground-muted">지정한 대상만</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAccess('public')}
              className={cn(
                'flex items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm',
                access === 'public'
                  ? 'border-accent bg-accent/5'
                  : 'border-border hover:bg-surface-muted',
              )}
            >
              <Globe className="size-4 text-foreground-muted" />
              <div>
                <div className="font-medium">전체</div>
                <div className="text-xs text-foreground-muted">로그인한 모든 사용자</div>
              </div>
            </button>
          </div>

          <div className="mt-3 flex items-center gap-1.5">
            <input
              type="text"
              readOnly
              value={shareUrl}
              className="flex-1 h-8 px-2 rounded-md border border-border bg-surface-muted text-xs font-mono"
            />
            <button
              type="button"
              onClick={onCopy}
              aria-label="URL 복사"
              className="grid size-8 place-items-center rounded-md border border-border hover:bg-surface-muted"
              title={copyState === 'copied' ? '복사됨' : 'URL 복사'}
            >
              {copyState === 'copied' ? (
                <Check className="size-3.5 text-success" />
              ) : (
                <Copy className="size-3.5 text-foreground-muted" />
              )}
            </button>
          </div>

          {error ? <div className="mt-2 text-xs text-destructive">{error}</div> : null}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-border">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="h-9 px-3 rounded-md text-sm text-foreground-muted hover:bg-surface-muted"
          >
            취소
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              saveMutation.mutate();
            }}
            disabled={saveMutation.isPending || sharesQuery.isLoading}
            className="h-9 px-4 rounded-md text-sm bg-accent text-accent-foreground hover:opacity-90 disabled:opacity-40"
          >
            {saveMutation.isPending ? '저장 중…' : '공유하기'}
          </button>
        </div>
      </div>
    </div>
  );
}
